import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/hooks/useAuth";
import { useUserTier } from "@/hooks/useUserTier";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Crown, Plus, Sun, Moon, FlaskConical, Eye, EyeOff, FileText } from "lucide-react";
import { BETA_BANNER_TEXT, BETA_MODE as RAW_BETA_MODE } from "@/lib/betaMode";
import { useEffectiveBetaMode } from "@/hooks/useEffectiveBetaMode";

const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { tierName, tierSlug } = useUserTier();
  const { theme, setTheme } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { effectiveBetaMode: BETA_MODE, isAdmin, adminPreviewBeta, toggleAdminPreviewBeta } = useEffectiveBetaMode();
  



  const { data: profile } = useQuery({
    queryKey: ["profile-display-name", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", user!.id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const displayName = profile?.display_name || user?.email || "";
  const initials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const isDashboard = location.pathname === "/dashboard";
  const isNewInvoice = location.pathname === "/invoice/new";

  return (
    <>
      {BETA_MODE && (
        <div className="bg-primary px-4 py-2.5 text-center text-sm font-medium text-primary-foreground">
          <div className="container flex items-center justify-center gap-2">
            <FlaskConical className="h-4 w-4 shrink-0" />
            <span>
              <strong>BETA PRIVAT</strong> — {BETA_BANNER_TEXT.replace("BETA PRIVAT — ", "")}
            </span>
          </div>
        </div>
      )}
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="container flex h-14 items-center justify-between gap-2">
          {/* Left: theme toggle + logo */}
          <div className="flex items-center gap-2 min-w-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 relative"
                  onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                >
                  <Sun className="h-4 w-4 rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
                  <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Schimbă tema</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{theme === "dark" ? "Mod luminos" : "Mod întunecat"}</TooltipContent>
            </Tooltip>
            {RAW_BETA_MODE && isAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={adminPreviewBeta ? "default" : "outline"}
                    size="sm"
                    className="h-8 gap-1 text-xs"
                    onClick={toggleAdminPreviewBeta}
                  >
                    {adminPreviewBeta ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    <span className="hidden md:inline">
                      {adminPreviewBeta ? "Ieși din preview Beta" : "Vezi cum arată în Beta"}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {adminPreviewBeta
                    ? "Revino la UI-ul real (post-launch)"
                    : "Previzualizează UI-ul de Beta (doar în această sesiune)"}
                </TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/");
                  }}
                  className="font-heading text-lg font-bold text-primary hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap shrink-0"
                >
                  EZ Factura
                </a>
              </TooltipTrigger>
              <TooltipContent>Pagina principală</TooltipContent>
            </Tooltip>
          </div>

          {/* Right: user controls */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">

            {user ? (
              <>
                {!isNewInvoice && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" onClick={() => navigate("/invoice/new")} className="gap-1">
                        <Plus className="h-4 w-4" />
                        <span className="hidden sm:inline">Factură Nouă</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Creează o factură nouă</TooltipContent>
                  </Tooltip>
                )}
                {!isDashboard && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate("/dashboard")}
                        className="gap-1 px-2 sm:px-3"
                        aria-label="Facturile Mele"
                      >
                        <FileText className="h-4 w-4 sm:hidden" />
                        <span className="hidden sm:inline">Facturile Mele</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Vezi lista de facturi</TooltipContent>
                  </Tooltip>
                )}
                <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="relative flex items-center gap-2 rounded-full hover:opacity-80 transition-opacity focus:outline-none"
                      onMouseEnter={() => setDropdownOpen(true)}
                    >
                      <Avatar className="h-8 w-8 border border-border">
                        <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium hidden sm:block max-w-[150px] truncate">
                        {displayName}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48" onMouseLeave={() => setDropdownOpen(false)}>
                    <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/settings")}>
                      Profilul Meu
                    </DropdownMenuItem>
                    {isAdmin && !adminPreviewBeta && (
                      <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/admin")}>
                        Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={async () => {
                        await signOut();
                        navigate("/");
                      }}
                    >
                      Deconectare
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {tierName && !BETA_MODE && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => navigate("/select-plan")}
                        className="focus:outline-none hover:opacity-80 transition-opacity"
                      >
                        <Badge
                          variant={tierSlug === "trial" ? "secondary" : "default"}
                          className="gap-1 text-xs cursor-pointer"
                        >
                          <Crown className="h-3 w-3" />
                          {tierName}
                        </Badge>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Schimbă planul de abonament</TooltipContent>
                  </Tooltip>
                )}
                {BETA_MODE && (
                  <Badge variant="secondary" className="gap-1 text-xs hidden sm:inline-flex">
                    <FlaskConical className="h-3 w-3" />
                    Beta
                  </Badge>
                )}
              </>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" onClick={() => navigate("/auth")}>
                    Conectează-te
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Autentifică-te sau creează un cont</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default AppHeader;